import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {toISODate} from "../Statistics/format.js";

/** Heutiges Datum in Europe/Berlin als ISO-Tag - unabhängig von der
 * Zeitzone des Rechners, auf dem der Browser läuft. */
function todayBerlinISO() {
    return new Intl.DateTimeFormat("en-CA", {timeZone: "Europe/Berlin"}).format(new Date());
}

function addDaysISO(iso, days) {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, m - 1, d + days);
    return toISODate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

function quarterRangeISO(iso) {
    const [y, m] = iso.split("-").map(Number);
    const startMonth = Math.floor((m - 1) / 3) * 3 + 1;
    const endMonth = startMonth + 2;
    const lastDay = new Date(y, endMonth, 0).getDate();
    return {from: toISODate(y, startMonth, 1), to: toISODate(y, endMonth, lastDay)};
}

function computePresetRange(preset) {
    const today = todayBerlinISO();
    switch (preset) {
        case "heute":
            return {from: today, to: today};
        case "7d":
            return {from: addDaysISO(today, -6), to: today};
        case "30d":
            return {from: addDaysISO(today, -29), to: today};
        case "quartal":
            return quarterRangeISO(today);
        default:
            return null;
    }
}

/**
 * Gemeinsamer Filterzustand der Nutzungsauswertung (Überblick, Mitarbeiter-
 * tabelle, Profil) - Vorbild ist useSalesFilters.js aus der Verkaufs-
 * statistik: Voreinstellung + freier Zeitraum, Zustand zentral, ein
 * fertiges Filter-Objekt für useStatisticsData.
 *
 * Die Mitarbeiterliste für die Auswahl kommt NICHT aus einem eigenen
 * Options-Endpunkt (den sieht die Spezifikation nicht vor), sondern wird -
 * genau wie useSalesFilters die Warengruppen-Rangfolge aus /statistics/by-group
 * mitliest - aus /nutzung/daten/mitarbeiter für den aktuellen Zeitraum
 * gewonnen, bewusst ohne Mitarbeiter-/Bereichsfilter, damit die Auswahl
 * sich nicht selbst einschränkt. Antwortet der Endpunkt (noch) nicht,
 * bleibt die Auswahl schlicht leer - kein Absturz der Filterleiste.
 */
export default function useNutzungFilter() {
    const initial = computePresetRange("7d");
    const [preset, setPreset] = useState("7d");
    const [von, setVon] = useState(initial.from);
    const [bis, setBis] = useState(initial.to);
    const [userId, setUserId] = useState("");
    const [bereich, setBereich] = useState("");
    const [employeeOptions, setEmployeeOptions] = useState([]);

    // Nur zur Dokumentation der Absicht vorgehalten (analog zum Vorbild) -
    // ein manuell gewählter Zeitraum wird von künftigen Voreinstellungs-
    // Ableitungen nicht mehr überschrieben, sobald es einen serverseitigen
    // Referenzzeitpunkt gäbe.
    const userHasChosenRangeRef = useRef(false);

    const selectPreset = useCallback((nextPreset) => {
        if (nextPreset === "frei") {
            userHasChosenRangeRef.current = true;
            setPreset("frei");
            return;
        }
        const range = computePresetRange(nextPreset);
        setPreset(nextPreset);
        if (range) {
            setVon(range.from);
            setBis(range.to);
        }
    }, []);

    const changeVon = useCallback((value) => {
        userHasChosenRangeRef.current = true;
        setPreset("frei");
        setVon(value);
    }, []);

    const changeBis = useCallback((value) => {
        userHasChosenRangeRef.current = true;
        setPreset("frei");
        setBis(value);
    }, []);

    useEffect(() => {
        if (!von || !bis) {
            return undefined;
        }
        let active = true;
        axios
            .get("/nutzung/daten/mitarbeiter", {params: {von, bis}})
            .then(({data}) => {
                if (!active) {
                    return;
                }
                const rows = Array.isArray(data) ? data : [];
                setEmployeeOptions(
                    rows
                        .filter((row) => row.user_id != null)
                        .map((row) => ({value: String(row.user_id), label: row.name || `Nutzer ${row.user_id}`}))
                );
            })
            .catch(() => {
                if (active) {
                    setEmployeeOptions([]);
                }
            });
        return () => {
            active = false;
        };
    }, [von, bis]);

    const filters = useMemo(
        () => ({
            von: von || undefined,
            bis: bis || undefined,
            user_id: userId || undefined,
            bereich: bereich || undefined,
        }),
        [von, bis, userId, bereich]
    );

    return {
        preset,
        selectPreset,
        von,
        bis,
        changeVon,
        changeBis,
        userId,
        setUserId,
        bereich,
        setBereich,
        employeeOptions,
        filters,
        filtersReady: Boolean(von && bis),
    };
}
